import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { AutoSizer, ScrollSync, Grid } from 'react-virtualized'
import scrollbarSize from 'dom-helpers/scrollbarSize'
import { fireOnSelectChange, resolveAllCheckedState, selectRow } from './select'
import { TableBottomSection } from './table_bottom_section'
import { DefaultSelected, getCellValue, getHeader, stickyStyles } from './common'
import { Pagination } from './Pagination'
import { Params } from './useTableHooks'
import { useUser } from '../../../hooks/useUser'
import { Menu, MenuItem } from '../../../components/ui/menu'
import { ContextMenu2 } from '@blueprintjs/popover2'
import '@blueprintjs/popover2/lib/css/blueprint-popover2.css'
import classNames from 'classnames'
import { Column, ExtraType } from '../../../types/table'
import { capitalCase } from 'change-case'

interface VirtualizedHTMLTableProps<T, Extra extends object> {
    loading?: boolean
    pagination?: Pagination
    params?: Params
    setParams?: any
    columns: Column<T, Extra>[]
    items?: T[]
    accordion?: any
    accordionRaw?: any // if true, we won't wrap the accordion content
    className?: any
    condensed?: boolean
    striped?: boolean
    empty: any
    paginationClassname: any
    htmlTableWrapperClassname: any
    reload: any
    mutateItems: any
    disableDefaultHtmlTableWrapperStyle?: boolean
    disableDefaultHtmlTableStyle?: boolean
    onSelectChange: any
    selectKey: any
    numericSelectKey: boolean
    selectedValues?: any
    canSelect?: any
    canSelectFromAllPages?: boolean
    showPerPage: boolean
    perPage?: number
    setPerPage?: any
    perPageLabel?: string
    stickyHeader?: boolean
    stickyFooter?: boolean
    totals?: any
    enableRowClick?: boolean
    overscanRowCount?: number
    overscanColumnCount?: number
    height?: number
    rowHeight?: number
    headerRowHeight?: number
    enableRowSelect?: boolean
    selectAllColumnWidth?: number
    width?: number
    hiddenColumns?: any
    setHiddenColumns?: (v: any) => void
    allColumns?: Column<T, Extra>[]
    extra: Extra
}

export const VirtualizedHTMLTable = <T, Extra extends object>({
    loading,
    pagination,
    params = {},
    setParams,
    columns,
    items,
    className,
    paginationClassname,
    reload,
    mutateItems,

    onSelectChange = () => {},
    selectKey = 'id',
    selectedValues = DefaultSelected,
    canSelect,
    canSelectFromAllPages = true,
    showPerPage = false, // if true, display per page component
    perPage = 15, // display 15 items per page
    setPerPage,
    perPageLabel = 'per page',

    stickyFooter = false,
    totals,

    overscanRowCount = 10,
    overscanColumnCount = 10,
    height = 500,
    rowHeight = 60,
    headerRowHeight = 50,
    enableRowSelect = true,
    selectAllColumnWidth = 100,
    width = undefined,

    hiddenColumns,
    setHiddenColumns,
    allColumns,
    extra,
}: VirtualizedHTMLTableProps<T, Extra>) => {
    const { screenshotMode, hasActiveSubscription, activeProfile } = useUser()
    const [showSelectAllWidget, setShowSelectAllWidget] = useState(false)
    const { stickyFooter: stickyFooterSection } = stickyStyles(false, stickyFooter)

    const allCheckedState = useMemo(() => {
        return resolveAllCheckedState(items, selectedValues, selectKey, canSelect)
    }, [selectedValues, items])

    // Emit onSelectChange event
    useEffect(() => fireOnSelectChange(selectedValues, onSelectChange), [selectedValues])

    useEffect(() => {
        if (setParams) {
            setParams({ ...params, perPage: perPage })
        }
    }, [perPage])

    let cols: Column<T, Extra>[] = columns

    if (enableRowSelect) {
        cols = [
            selectRow(
                canSelect,
                canSelectFromAllPages,
                showSelectAllWidget,
                setShowSelectAllWidget,
                selectedValues,
                allCheckedState,
                pagination,
                items,
                selectKey,
                onSelectChange,
                {
                    headerWidth: selectAllColumnWidth,
                    width: selectAllColumnWidth,
                },
            ),
            ...columns,
        ]
    }

    const tableBottomSection = useMemo(() => {
        return (
            <TableBottomSection
                pagination={pagination}
                paginationClassname={paginationClassname}
                stickyFooterSection={stickyFooterSection}
                loading={loading}
                showPerPage={showPerPage}
                perPage={perPage}
                setPerPage={setPerPage}
                perPageLabel={perPageLabel}
                params={params}
                setParams={setParams}
            />
        )
    }, [pagination, loading, perPage, perPageLabel])

    const columnCount = cols.length
    const rowCount = (items ?? []).length

    const renderHeaderCell = useCallback(
        (props) => {
            return _renderHeaderCell({
                ...props,
                columns: cols,
                sort: params.sort,
                setParams,
                totals,
                enableRowSelect,
                hiddenColumns,
                allColumns: allColumns ?? columns,
                setHiddenColumns,
            })
        },
        [cols, params.sort, setParams, totals, enableRowSelect, hiddenColumns, allColumns, columns],
    )

    return (
        <div className={classNames('custom-table', className)}>
            <ScrollSync>
                {({ onScroll, scrollLeft, ...args }) => {
                    const hasHorizontalScrollbar = args.clientWidth < args.scrollWidth
                    return (
                        <div
                            className="GridRow"
                            style={{
                                width: width ?? '',
                                margin: '0 auto',
                            }}
                        >
                            <div className="GridColumn">
                                <AutoSizer disableHeight>
                                    {({ width }) => (
                                        <div id="body-grid-parent">
                                            <div
                                                id="header-grid"
                                                style={{
                                                    color: '#000',
                                                    height: totals
                                                        ? headerRowHeight * 2
                                                        : headerRowHeight,
                                                    width: width,
                                                    // width: width - scrollbarSize(),
                                                    // paddingRight:
                                                }}
                                            >
                                                <Grid
                                                    style={{
                                                        paddingRight: hasHorizontalScrollbar
                                                            ? scrollbarSize() + 'px'
                                                            : undefined,
                                                    }}
                                                    className="HeaderGrid"
                                                    columnWidth={({ index }) =>
                                                        _calculateColumnWidth({
                                                            column: cols[index],
                                                        })
                                                    }
                                                    columnCount={columnCount}
                                                    height={
                                                        totals
                                                            ? headerRowHeight * 2
                                                            : headerRowHeight
                                                    }
                                                    overscanColumnCount={overscanColumnCount}
                                                    cellRenderer={renderHeaderCell}
                                                    rowHeight={headerRowHeight}
                                                    rowCount={totals ? 2 : 1}
                                                    scrollLeft={scrollLeft}
                                                    width={width}
                                                />
                                            </div>
                                            <div
                                                id="body-grid"
                                                style={{
                                                    color: '#000',
                                                    height: height,
                                                    width: width,
                                                }}
                                            >
                                                <Grid
                                                    className="BodyGrid"
                                                    columnWidth={({ index }) =>
                                                        _calculateColumnWidth({
                                                            column: cols[index],
                                                        })
                                                    }
                                                    columnCount={columnCount}
                                                    height={height}
                                                    onScroll={onScroll}
                                                    overscanColumnCount={overscanColumnCount}
                                                    overscanRowCount={overscanRowCount}
                                                    cellRenderer={(props) => {
                                                        let componentProps = null
                                                        const col = cols[props.columnIndex]
                                                        if (col && typeof col !== 'string') {
                                                            componentProps = col.props
                                                        }
                                                        return _renderBodyCell({
                                                            ...props,
                                                            columns: cols,
                                                            items: items ?? [],
                                                            extra: {
                                                                screenshotMode,
                                                                hasActiveSubscription,
                                                                reload,
                                                                mutateItems,
                                                                props: componentProps,
                                                                impact_multiplier:
                                                                    activeProfile?.impact_multiplier,
                                                                col,
                                                                ...extra,
                                                            },
                                                        })
                                                    }}
                                                    rowHeight={rowHeight}
                                                    rowCount={rowCount}
                                                    width={width}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </AutoSizer>
                            </div>
                            <div className="mt-2">{tableBottomSection}</div>
                        </div>
                    )
                }}
            </ScrollSync>
        </div>
    )
}

export const getVirtualizedHeader = (
    column: Column<any, any>,
    sort: string | null,
    setParams: any,
) => {
    let headerWidth: number | undefined
    if (typeof column !== 'string') {
        headerWidth = column.headerWidth ? column.headerWidth : column.width
    }

    return getHeader(column, sort, setParams, false, {
        minWidth: headerWidth,
        maxWidth: headerWidth,
    })
}

interface BodyCellProps<T, Extra extends object> {
    columnIndex: number
    key: string
    style: any
    rowIndex: number
    columns: Column<T, Extra>[]
    items: T[]
    extra: ExtraType & Extra
}

// Body
const _renderBodyCell = <T, Extra extends object>({
    columnIndex,
    key,
    rowIndex,
    style,
    columns,
    items,
    extra,
}: BodyCellProps<T, Extra>) => {
    const rowClass = rowIndex % 2 === 0 ? 'evenRow' : 'oddRow'
    const customStyle = { ...style, ...style.cell }

    return (
        <div className={'cell' + ' ' + rowClass + ' p-2'} key={key} style={customStyle}>
            {getCellValue(columns[columnIndex], items[rowIndex], extra)}
        </div>
    )
}

interface HeaderCellProps<T, Extra extends object> {
    columnIndex: number
    key: string
    style: any
    rowIndex: number
    columns: Column<T, Extra>[]
    sort: string | null
    setParams: any
    totals: any
    hiddenColumns?: any
    allColumns?: Column<T, Extra>[]
    setHiddenColumns?: (v: any) => void
}

// Render Header
const _renderHeaderCell = <T, Extra extends object>({
    columnIndex,
    key,
    style,
    rowIndex,
    columns,
    sort,
    setParams,
    totals,
    hiddenColumns,
    allColumns,
    setHiddenColumns,
}: HeaderCellProps<T, Extra>) => {
    const column = columns[columnIndex]
    let header = null
    if (rowIndex === 0) {
        header = getVirtualizedHeader(column, sort, setParams)
    } else if (rowIndex === 1 && totals) {
        header = renderTotals(column, totals, columnIndex === 0)
    }

    if (!header) {
        return
    }

    // Add context menu
    if (rowIndex == 0) {
        const formatColumnTitle = (col: Column<T, Extra>) => {
            if (typeof col === 'string') {
                return capitalCase((col ?? '').replace('_', ' '))
            }
            return (
                col.text_title ??
                (typeof col.title === 'string'
                    ? col.title
                    : capitalCase((col.key ?? '').replace('_', ' ')))
            )
        }

        const hiddenColumnItems =
            hiddenColumns && hiddenColumns.length > 0 && allColumns
                ? allColumns
                      .filter((col) => {
                          const colKey = typeof col === 'string' ? col : col.key
                          return hiddenColumns.includes(colKey)
                      })
                      .map((col) => {
                          const colKey = typeof col === 'string' ? col : col.key
                          const title = formatColumnTitle(col)
                          return (
                              <MenuItem
                                  key={colKey}
                                  text={title}
                                  onClick={() => setHiddenColumns?.(colKey)}
                              />
                          )
                      })
                : []

        return (
            <ContextMenu2
                key={key}
                content={
                    <Menu key={key}>
                        <MenuItem
                            key={key}
                            text="Hide Column"
                            onClick={() => setHiddenColumns?.(column.key)}
                        />
                        {hiddenColumnItems.length > 0 && (
                            <MenuItem text="Unhide Columns">{hiddenColumnItems}</MenuItem>
                        )}
                    </Menu>
                }
            >
                <div className="headerCell" key={key} style={style}>
                    {header}
                </div>
            </ContextMenu2>
        )
    }

    return (
        <div className="headerCell" key={key} style={style}>
            {header}
        </div>
    )
}

const _calculateColumnWidth = ({ column }: { column: Column<any, any> }): number => {
    const defaultColumnWidth = 150
    if (typeof column !== 'string' && column.width) {
        return column.width
    }

    return defaultColumnWidth
}

export const renderTotals = (
    column: Column<any, any>,
    totals: any,
    first: boolean,
): JSX.Element => {
    const k = typeof column === 'string' ? column : column.total_key ? column.total_key : column.key
    let v = totals[k]
    if (!v) {
        return <div />
    }

    if (typeof column !== 'string' && column.totalFormatter) {
        v = column.totalFormatter(v)
    } else if (typeof column !== 'string' && column.formatter) {
        v = column.formatter(v)
    }

    if (first) {
        v = ['Total: ', v]
    }

    return (
        <div className="p-2" style={{ textAlign: 'left', verticalAlign: 'middle' }}>
            {v}
        </div>
    )
}
